import React from "react";
import LegalShell, { LegalSection, LegalList } from "@/components/LegalShell";

// ============================================================================
// /disclaimer — public legal page (no login required).
// Operational + safety disclaimer for a calculation-support tool.
// ============================================================================
const CONTACT_EMAIL = "rigsentinel@gmail.com";

export default function Disclaimer() {
  return (
    <LegalShell
      title="Disclaimer"
      subtitle="RigSentinel is a deterministic calculation-support and reference tool. It is not a real-time well-control instruction system, not a regulatory certification system, and not a substitute for qualified engineering judgment."
      lastUpdated="February 2026"
      testId="page-disclaimer"
    >
      <LegalSection id="general" title="1. General Calculation Disclaimer">
        <p>
          Every calculation, conversion, worksheet, and report draft produced
          by RigSentinel is calculation support only. Each output must be
          independently verified by qualified personnel against the operator's
          approved procedures, manufacturer (OEM) datasheets, well-specific
          data, and all applicable regulations before any operational use.
        </p>
        <p>
          Always check input values, units, formulas, assumptions, intermediate
          steps, and final results. Do not treat any RigSentinel output as a
          directive, certification, or guarantee.
        </p>
      </LegalSection>

      <LegalSection id="well-control" title="2. Well Control">
        <p>
          Well-control calculators (kill sheet drafts, MAASP, kick tolerance,
          slow-circulation rate analysis, leak-off / formation strength, pump
          pressure schedules, choke schedules, kill-weight calculations) are
          computational support only and are NOT a live well-control
          instruction system.
        </p>
        <p>
          Live well-control decisions must always be made by certified
          well-control personnel using approved company procedures, the rig's
          IWCF/IADC-aligned well-control manual, applicable regulatory
          requirements, and current well data. Never operate the choke, pumps,
          BOP, diverter, or any well-control equipment based solely on output
          from this tool.
        </p>
      </LegalSection>

      <LegalSection id="subsea" title="3. Subsea Operations">
        <p>
          Subsea well-control and subsea-stack calculations require
          rig-specific subsea stack data, marine riser geometry, choke/kill
          line lengths and IDs, control-system response, hydrate-management
          procedures, mudline-suspension data, and qualified subsea
          well-control review. RigSentinel outputs are not a substitute for
          the rig's approved subsea well-control program or OEM data.
        </p>
      </LegalSection>

      <LegalSection id="bop" title="4. BOP and Well-Control Equipment">
        <p>
          Any reference data, capacities, working pressures, ratings, response
          volumes, accumulator volumes, regulator settings, or function /
          response times shown in this tool are illustrative reference values
          only. Always use rig-, well-, and equipment-specific OEM datasheets,
          current BOP test records, API/IADC/IOGP standards, regulatory
          requirements, and the operator's approved well-control program.
        </p>
      </LegalSection>

      <LegalSection id="reports" title="5. Reports and AI Polishing">
        <p>
          The Reports &amp; Field Documentation Hub generates structured draft
          documents from your input. The AI-assisted "Polish Report" and
          "Translate &amp; Polish" features rewrite your draft text for clarity
          and tone; they do <strong>not</strong> certify, validate, approve, or
          conclude anything about the underlying operation. AI output is a
          drafting aid only.
        </p>
        <LegalList
          items={[
            "AI output must be reviewed and approved by a qualified person before submission, signing, or distribution.",
            "AI output must never be used as a substitute for required regulatory documentation or certified records.",
            "Numerical values in AI-polished text must be re-verified against the original source data.",
          ]}
        />
      </LegalSection>

      <LegalSection id="responsibility" title="6. User Responsibility">
        <p>
          You are solely responsible for:
        </p>
        <LegalList
          items={[
            "Selecting the correct calculator, report template, units, and reference data for your scenario.",
            "Verifying all inputs and outputs against approved sources.",
            "All operational, safety, environmental, regulatory, and commercial consequences of any decision you make in connection with the application.",
          ]}
        />
      </LegalSection>

      <LegalSection id="no-warranty" title="7. No Warranty">
        <p>
          RigSentinel is provided "as is" and "as available", without
          warranties of any kind, express or implied. We do not warrant that
          the application will be uninterrupted, error-free, or that outputs
          will be accurate in every operational context.
        </p>
      </LegalSection>

      <LegalSection id="acknowledgement" title="8. Acknowledgement">
        <p>
          By using RigSentinel, you acknowledge that you have read,
          understood, and accept this Disclaimer, the Terms of Service, and
          the in-app Safety Center notices.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="9. Contact">
        <p>
          Safety, accuracy, or content questions can be sent to{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="disclaimer-contact-email"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalShell>
  );
}
